/**
 * Route classification for analytics gating.
 *
 * Next.js route groups (`(dashboard)`, `(public)`, `(auth)`) do not appear in
 * the URL, so these prefixes match the real pathnames served to the browser.
 */

/**
 * Routes where session replay is suppressed regardless of consent. These
 * surfaces expose account, subscription and payment context that must never be
 * filmed:
 *  - `/settings` and everything under it (profile, subscription management, the
 *    billing-portal return, plan changes);
 *  - `/pricing` and any checkout/billing surface where a user selects a plan or
 *    is handed off to payment.
 */
const SENSITIVE_ROUTE_PREFIXES = ['/settings', '/pricing', '/billing', '/checkout'];

/** Authenticated admin UI. Pageviews and autocapture stay off here. */
const ADMIN_ROUTE_PREFIXES = ['/admin'];

/**
 * Public marketing / unauthenticated surfaces. Autocapture is disabled here;
 * the authenticated product (everything else) re-enables it.
 */
const PUBLIC_MARKETING_ROUTE_PREFIXES = [
  '/pricing',
  '/legal',
  '/login',
  '/signup',
  '/reset-password',
  // Destination reports are marketing surfaces: autocapture stays off and
  // funnel events are fired explicitly with safeCapture instead.
  '/destinations',
  // Blog is a public acquisition surface. Leaving it off this list classified
  // it as the authenticated app: autocapture on, and (before consent-based
  // persistence) memory-only ids.
  '/blog',
];

function matchesPrefix(pathname: string | null, prefixes: readonly string[]): boolean {
  if (!pathname) {
    return false;
  }

  return prefixes.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * True when replay must be suppressed on this route. A `null` pathname is
 * treated as non-sensitive (replay gating still requires explicit consent).
 */
export function isSensitiveRoute(pathname: string | null): boolean {
  return matchesPrefix(pathname, SENSITIVE_ROUTE_PREFIXES);
}

/**
 * True for `/admin` and its subpaths. These are internal tools; product
 * pageviews and autocapture must not count them. A null pathname is not admin.
 */
export function isAdminRoute(pathname: string | null): boolean {
  return matchesPrefix(pathname, ADMIN_ROUTE_PREFIXES);
}

/**
 * True for public marketing routes and the exact site root (`/`), where
 * autocapture is disabled. A `null` pathname is treated as public (the safer
 * default: no autocapture until we know we are on the authenticated side).
 */
export function isPublicMarketingRoute(pathname: string | null): boolean {
  if (pathname === null || pathname === '/') {
    return true;
  }

  return matchesPrefix(pathname, PUBLIC_MARKETING_ROUTE_PREFIXES);
}
